
import { Transaction, TransactionFormData } from '@/types';
import { 
  generateTransactionId, 
  validateTransactionData, 
  normalizeTransactionData
} from '@/utils/transactionUtils';
import { logger } from '@/utils/logger';

export const createTransaction = (transactionData: TransactionFormData): Transaction => {
  logger.debug('[TransactionOperations] Creating transaction', { 
    type: transactionData.type,
    status: transactionData.status 
  });
  
  // Verificar se amount está definido e válido antes da normalização
  if (transactionData.amount === undefined || transactionData.amount === null) {
    console.error('[TransactionOperations] Invalid amount detected:', transactionData.amount);
    throw new Error('Dados inválidos: Valor é obrigatório');
  }
  
  // Normalizar dados de entrada
  const normalizedData = normalizeTransactionData(transactionData);
  
  // Validar dados
  const validation = validateTransactionData(normalizedData);
  if (!validation.isValid) {
    console.error('[TransactionOperations] Validation errors:', validation.errors);
    throw new Error(`Dados inválidos: ${validation.errors.join(', ')}`);
  }
  
  // Gerar UUID válido
  const transactionId = generateTransactionId();
  
  // Check if transaction date is in the future
  const today = new Date().toISOString().split('T')[0];
  const isFutureTransaction = normalizedData.date > today;
  
  // FIXED: Prioritize user-provided status over automatic rules
  let status: 'paid' | 'pending' | 'partial' = 'paid'; // default fallback
  
  // Check if user provided explicit status (including 'paid')
  const hasExplicitStatus = normalizedData.status && 
    typeof normalizedData.status === 'string' &&
    ['paid', 'pending', 'partial'].includes(normalizedData.status);
  
  // Check if this is an installment import (has installmentNumber and installments)
  const isInstallmentImport = normalizedData.installmentNumber && normalizedData.installments;
  
  if (hasExplicitStatus && isInstallmentImport) {
    // For installment imports, ALWAYS use the explicit status provided
    status = normalizedData.status;
  } else if (hasExplicitStatus && normalizedData.status !== 'paid') {
    // For non-installments, use explicit status only if it's not 'paid' (to maintain backward compatibility)
    status = normalizedData.status;
  } else {
    // No explicit status provided or default 'paid', apply automatic rules
    
    // Credit card expenses are always pending until bill is paid
    if (normalizedData.paymentMethod === 'cartao' && normalizedData.type === 'despesa') {
      status = 'pending';
    }
    // Future transactions should be pending
    else if (isFutureTransaction) {
      status = 'pending';
    } else {
      status = 'paid';
    }
  }

  const newTransaction: Transaction = {
    id: transactionId,
    type: normalizedData.type,
    amount: normalizedData.amount,
    description: normalizedData.description,
    categoryId: normalizedData.categoryId,
    date: normalizedData.date,
    paymentMethod: normalizedData.paymentMethod,
    cardId: normalizedData.cardId,
    accountId: normalizedData.accountId || undefined,
    account: normalizedData.account || '',
    bank: normalizedData.bank || '',
    status: status,
    createdAt: new Date().toISOString(),
    
    // Campos de parcelas (compatíveis com DB)
    installmentNumber: normalizedData.installmentNumber,
    installments: normalizedData.installments,
    parentTransactionId: normalizedData.originalTransactionId || undefined,
    
    // Outros campos
    isInstallment: normalizedData.isInstallment || false,
    
    // Campos FIFO - para parcelas já pagas importadas via OFX
    originalAmount: normalizedData.originalAmount,
    paidPortion: normalizedData.paidPortion,
    
    // NOVO: Propagar billDueDate para correlação robusta
    billDueDate: normalizedData.billDueDate,
  };

  logger.debug('[TransactionOperations] Transaction created', { 
    id: newTransaction.id, 
    status: newTransaction.status 
  });
  
  return newTransaction;
};

export const getTransactionsByCard = (transactions: Transaction[], cardId: string): Transaction[] => {
  return transactions.filter(transaction => transaction.cardId === cardId);
};

export const getTransactionsByAccount = (transactions: Transaction[], accountId: string): Transaction[] => {
  return transactions.filter(transaction => transaction.accountId === accountId);
};

export const getRecentTransactions = (transactions: Transaction[], limit: number = 10): Transaction[] => {
  return transactions
    .sort((a, b) => {
      // Ordenar por createdAt se disponível, senão usar date
      const aCreatedAt = a.createdAt ? new Date(a.createdAt).getTime() : new Date(a.date).getTime();
      const bCreatedAt = b.createdAt ? new Date(b.createdAt).getTime() : new Date(b.date).getTime();
      return bCreatedAt - aCreatedAt;
    })
    .slice(0, limit);
};
